export default {
  mounted() {
    this.$nextTick(() => {
      if (!this.$el || !(this.$el instanceof Element)) return;
      this.enhanceAccessibility();
      this.fixAriaSortAttributes()
    });
  },

  updated() {
    // 组件更新后也可能需要重新处理，特别是动态内容
    this.$nextTick(() => {
      if (!this.$el || !(this.$el instanceof Element)) return;
      this.enhanceAccessibility();
      this.fixAriaSortAttributes()
    });
  },
  
  methods: {
    /**
     * 强化无障碍功能，包含为按钮、链接、表单元素和图片添加无障碍属性
     */
    enhanceAccessibility() {
      // 为按钮添加aria-label
      this.addButtonLabels();
      
      // 为链接添加aria-label
      this.addLinkLabels();
      
      // 为表单元素添加aria-label
      this.addFormLabels();
      
      // 为图片添加alt文本
      this.addImageAlts();
      
      // 为复选框添加aria-checked属性和可访问性标签
      this.addCheckboxAriaChecked();
      
      // 为分页按钮添加可访问性标签
      this.addPaginatorLabels();
    },
    
    addButtonLabels() {
      const buttons = this.$el.querySelectorAll('button, [role="button"]');
      buttons.forEach(button => {
        if (!button.getAttribute('aria-label')) {
          const text = button.textContent.trim();
          const existingLabel = button.getAttribute('aria-label');
          
          if (text && !existingLabel) {
            button.setAttribute('aria-label', text);
          } else if (!text && !existingLabel) {
            // 对于图标按钮，使用title或自定义逻辑
            const title = button.getAttribute('title');
            if (title) {
              button.setAttribute('aria-label', title);
            }
          }
        }
      });
    },
    
    addLinkLabels() {
      const links = this.$el.querySelectorAll('a');
      links.forEach(link => {
        if (!link.getAttribute('aria-label')) {
          const text = link.textContent.trim();
          if (text) {
            link.setAttribute('aria-label', `链接到: ${text}`);
          }
        }
      });
    },
    
    addFormLabels() {
      const inputs = this.$el.querySelectorAll('input, textarea, select');
      
      // 为不同类型的输入元素创建计数器
      const typeCounters = {
        input: 1,
        textarea: 1,
        select: 1
      };
      
      inputs.forEach((input) => {
        if (!input.getAttribute('aria-label')) {
          // 优先使用name属性
          const name = input.getAttribute('name');
          if (name) {
            input.setAttribute('aria-label', name);
            return;
          }
          
          // 其次尝试通过id查找关联的label
          if (input.id) {
            const label = document.querySelector(`label[for="${input.id}"]`);
            if (label && label.textContent.trim()) {
              input.setAttribute('aria-label', label.textContent.trim());
              return;
            }
          }
          
          // 如果没有id或者通过id没找到label，尝试其他查找方式
          const label = this.findLabelForInput(input);
          if (label && label.textContent.trim()) {
            input.setAttribute('aria-label', label.textContent.trim());
          } else {
            let defaultLabel;
            // 最后优先使用placeholder作为aria-label
            const placeholder = input.getAttribute('placeholder');
            if (placeholder && placeholder.trim()) {
              defaultLabel = placeholder;
            } 
            // 再根据元素类型使用不同的默认aria-label
            else {
              const tagName = input.tagName.toLowerCase();

              if (tagName === 'input') {
                defaultLabel = `input ${typeCounters.input++}`;
              } else if (tagName === 'textarea') {
                defaultLabel = `textarea ${typeCounters.textarea++}`;
              } else if (tagName === 'select') {
                defaultLabel = `select ${typeCounters.select++}`;
              } else {
                defaultLabel = `form element`;
              }
            }
            
            input.setAttribute('aria-label', defaultLabel);
          }
        }
      });
    },
    
    findLabelForInput(input) {
      // 查找关联的label元素
      if (input.id) {
        return document.querySelector(`label[for="${input.id}"]`);
      }
      
      // 查找父元素内的label
      const parent = input.closest('.form-group, .input-group');
      if (parent) {
        return parent.querySelector('label');
      }
      
      return null;
    },
    
    addImageAlts() {
      const images = this.$el.querySelectorAll('img');
      images.forEach(img => {
        if (!img.getAttribute('alt') && !img.getAttribute('aria-hidden')) {
          // 为装饰性图片添加空alt，为内容图片添加描述
          const isDecorative = img.getAttribute('role') === 'presentation' || img.getAttribute('aria-hidden') === 'true';
          
          if (isDecorative) {
            img.setAttribute('alt', '');
            img.setAttribute('aria-hidden', 'true');
          } else {
            // 尝试从文件名或附近文本获取描述
            const src = img.getAttribute('src') || '';
            const fileName = src.split('/').pop().split('.')[0];
            img.setAttribute('alt', fileName || '图片');
          }
        }
      });
    },
    
    /**
     * 为自定义复选框，例如：<div role="checkbox"></div> 添加aria-checked属性和可访问性标签
     * 解决Required ARIA attribute not present: aria-checked问题
     * 和Element does not have text that is visible to screen readers问题
     */
    addCheckboxAriaChecked() {
      // 查找所有具有role="checkbox"的元素
      const checkboxes = this.$el.querySelectorAll('[role="checkbox"]');
      
      checkboxes.forEach(checkbox => {
        // 如果缺少aria-checked属性，则添加默认值false
        if (!checkbox.hasAttribute('aria-checked')) {
          checkbox.setAttribute('aria-checked', 'false');
        }
        
        // 如果缺少aria-label属性，尝试从附近文本或父元素获取标签
        if (!checkbox.getAttribute('aria-label')) {
          // 尝试查找关联的label元素
          const label = this.findLabelForCheckbox(checkbox);
          if (label && label.textContent.trim()) {
            checkbox.setAttribute('aria-label', label.textContent.trim());
          } else {
            // 如果没有找到明确的label，使用默认的复选框标签
            checkbox.setAttribute('aria-label', '复选框');
          }
        }
      });
    },
    
    /**
     * 为复选框查找关联的label元素
     */
    findLabelForCheckbox(checkbox) {
      // 首先尝试通过aria-labelledby属性查找
      const labelledBy = checkbox.getAttribute('aria-labelledby');
      if (labelledBy) {
        return document.getElementById(labelledBy);
      }
      
      // 尝试查找相邻的文本元素或label
      const parent = checkbox.parentElement;
      if (parent) {
        // 查找父元素内的label
        const label = parent.querySelector('label');
        if (label) return label;
        
        // 查找包含文本的相邻元素
        const siblings = Array.from(parent.children);
        const textSibling = siblings.find(sibling => 
          sibling !== checkbox && sibling.textContent && sibling.textContent.trim()
        );
        if (textSibling) return textSibling;
      }
      
      // 查找前面的label元素
      const previousElement = checkbox.previousElementSibling;
      if (previousElement && previousElement.tagName.toLowerCase() === 'label') {
        return previousElement;
      }
      
      return null;
    },

    /**
     * 为分页按钮添加可访问性标签，解决Element does not have inner text that is visible to screen readers问题
     */
    addPaginatorLabels() {
      // 查找所有分页按钮元素
      const paginatorButtons = this.$el.querySelectorAll('.p-paginator-prev, .p-paginator-next, .p-paginator-first, .p-paginator-last');
      
      paginatorButtons.forEach(button => {
        // 如果缺少aria-label属性，则根据按钮类型添加相应的标签
        if (!button.getAttribute('aria-label')) {
          let label = '';
          
          if (button.classList.contains('p-paginator-prev')) {
            label = '上一页';
          } else if (button.classList.contains('p-paginator-next')) {
            label = '下一页';
          } else if (button.classList.contains('p-paginator-first')) {
            label = '第一页';
          } else if (button.classList.contains('p-paginator-last')) {
            label = '最后一页';
          }
          
          if (label) {
            button.setAttribute('aria-label', label);
          }
        }
        
        // 为禁用状态的分页按钮添加适当的aria属性
        if (button.disabled || button.classList.contains('p-disabled')) {
          button.setAttribute('aria-disabled', 'true');
        } else {
          button.setAttribute('aria-disabled', 'false');
        }
      });
    },
    
    // 工具方法：为元素添加aria-label，示例：this.$setAriaLabel(element, '这是一个按钮');
    $setAriaLabel(element, label) {
      if (element && label) {
        element.setAttribute('aria-label', label);
      }
    },
    
    // 工具方法：为元素添加role，示例：this.$setRole(element, 'button');
    $setRole(element, role) {
      if (element && role) {
        element.setAttribute('role', role);
      }
    },

    /* ******************************************************************************* */

    /**
    * 修复aria-sort属性冲突问题，解决ARIA attribute is not allowed: aria-sort=none问题
    */
    fixAriaSortAttributes() {
      // 查找所有带有 aria-sort 属性的 th 元素
      const sortableHeaders = this.$el.querySelectorAll('th[aria-sort]');

      sortableHeaders.forEach(header => {
        console.log(header, header.getAttribute('role'));
        // 确保 th 元素具有正确的角色
        if (header.getAttribute('role') === 'cell') {
          // 移除冲突的 role="cell"，恢复 th 默认的 columnheader 角色
          header.removeAttribute('role');
          // 或者，显式设置为正确的角色
          // header.setAttribute('role', 'columnheader');
        }
        
        // 移除 aria-sort="none" 属性，解决可访问性警告
        if (header.getAttribute('aria-sort') === 'none') {
          header.removeAttribute('aria-sort');
        }
      });
    }
  }
};